import React, { useState, useEffect } from 'react';
import { motion, useMotionValue, useTransform, animate, MotionValue } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { app } from '../firebaseConfig';
import { User } from 'firebase/auth';
import {
  UserCircleIcon,
  StarIcon,
  GiftIcon,
  ClockIcon,
  ArrowRightIcon,
  ArrowRightOnRectangleIcon,
  QrCodeIcon,
  TrophyIcon,
  ShoppingBagIcon,
} from '@heroicons/react/24/outline';
import { Dialog } from '@headlessui/react';
import { QRCodeSVG } from 'qrcode.react';
import PageContainer from '../components/layout/PageContainer';
import { calcularNivel, xpParaProximoNivel, calcularProgressoNivel } from '../utils/xpUtils';
import LoadingSpinner from '../components/common/LoadingSpinner';

function maskPhone(phone: string) {
  if (!phone) return '';
  // Aceita DDD com 2 dígitos e número com 9 dígitos
  return phone.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
}

function getInitials(name: string) {
  if (!name) return '';
  const names = name.trim().split(' ');
  if (names.length === 1) return names[0].slice(0, 2).toUpperCase();
  return (names[0][0] + names[names.length - 1][0]).toUpperCase();
}

function stringToColor(str: string) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  // Generate a pastel color
  const hue = hash % 360;
  return `hsl(${hue}, 70%, 60%)`;
}

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const { user, setUser, refreshToken } = useAuth();
  const [userData, setUserData] = React.useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isQRModalOpen, setIsQRModalOpen] = useState(false);
  const [isComingSoonModalOpen, setIsComingSoonModalOpen] = useState(false);
  const [comingSoonFeature, setComingSoonFeature] = useState('');
  const [transactions, setTransactions] = useState<any[]>([]);
  const [loadingTransactions, setLoadingTransactions] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Add motion values for animations
  const xpMotionValue = useMotionValue(0);
  const levelMotionValue = useMotionValue(0);
  const progressMotionValue = useMotionValue(0);

  // Transform motion values to display values
  const displayXp = useTransform(xpMotionValue, (latest) => Math.round(latest));
  const displayLevel = useTransform(levelMotionValue, (latest) => Math.round(latest));
  const displayProgress = useTransform(progressMotionValue, (latest) => `${latest}%`);

  // Create a component to display motion values
  const MotionValueDisplay = ({ value }: { value: MotionValue<number> }) => {
    const display = useTransform(value, (latest) => Math.round(latest));
    return <motion.span>{display}</motion.span>;
  };

  // Mock de transações recentes
  const mockRecentTransactions = [
    {
      id: 1,
      business: 'Seus pontos ganhos aqui',
      points: 0,
      date: '2025-06-06',
      type: 'earned',
    }
  ];

  React.useEffect(() => {
    const fetchUserData = async () => {
      if (user?.uid) {
        const db = getFirestore(app);
        const userRef = doc(db, 'users', user.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          setUserData(userSnap.data());
          // Animate values when data is loaded
          animate(xpMotionValue, userSnap.data().balance || 0, { duration: 1.5 });
          animate(levelMotionValue, calcularNivel(userSnap.data().balance || 0), { duration: 1.5 });
          animate(progressMotionValue, calcularProgressoNivel(userSnap.data().balance || 0), { duration: 1.5 });
        }
      }
      setLoading(false);
    };
    fetchUserData();
  }, [user]);

  React.useEffect(() => {
    let isMounted = true;

    const fetchTransactions = async () => {
      if (!user?.uid) return;
      
      try {
        setLoadingTransactions(true);
        setError(null);

        // Use existing token first
        let token = user.token || undefined;
        
        // Only refresh if no token exists
        if (!token) {
          try {
            const newToken = await refreshToken();
            if (newToken) {
              token = newToken;
            }
          } catch (refreshError) {
            console.error('Error refreshing token:', refreshError);
            // If refresh fails, try to continue with existing token
            if (!token) {
              throw new Error('Failed to get token');
            }
          }
        }

        const response = await fetch('https://getusertransactions-b7jrylvg3a-uc.a.run.app', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            userId: user.uid,
            limit: 3
          })
        });

        if (!response.ok) {
          if (response.status === 401) {
            // If unauthorized, try to refresh token once
            try {
              const newToken = await refreshToken();
              if (!newToken) {
                throw new Error('Failed to refresh token');
              }
              // Retry the request with new token
              const retryResponse = await fetch('https://getusertransactions-b7jrylvg3a-uc.a.run.app', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${newToken}`
                },
                body: JSON.stringify({
                  userId: user.uid,
                  limit: 3
                })
              });
              if (!retryResponse.ok) {
                throw new Error('Failed to fetch transactions after token refresh');
              }
              const data = await retryResponse.json();
              if (isMounted) {
                const transactionsData = Array.isArray(data) ? data : data.transactions || [];
                setTransactions(transactionsData);
              }
              return;
            } catch (retryError) {
              throw new Error('Failed to fetch transactions');
            }
          }
          throw new Error('Failed to fetch transactions');
        }

        const data = await response.json();
        console.log('Transactions response:', data);

        if (isMounted) {
          const transactionsData = Array.isArray(data) ? data : data.transactions || [];
          setTransactions(transactionsData);
        }
      } catch (error) {
        console.error('Error fetching transactions:', error);
        if (isMounted) {
          setError('Erro ao carregar transações. Tente novamente mais tarde.');
        }
      } finally {
        if (isMounted) {
          setLoadingTransactions(false);
        }
      }
    };

    fetchTransactions();

    return () => {
      isMounted = false;
    };
  }, [user]);

  const handleLogout = () => {
    setUser(null);
    navigate('/');
  };

  const handleComingSoon = (feature: string) => {
    setComingSoonFeature(feature);
    setIsComingSoonModalOpen(true);
  };

  if (loading) {
    return (
      <PageContainer>
        <div className="flex justify-center items-center min-h-screen">
          <LoadingSpinner />
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="space-y-6">
        {/* Profile Header */}
        <div className="relative">
          <div className="h-32 bg-gradient-to-r from-primary to-accent rounded-xl" />
          <div className="absolute -bottom-16 left-1/2 transform -translate-x-1/2">
            <div 
              className="w-32 h-32 rounded-full border-4 border-background overflow-hidden flex items-center justify-center text-4xl font-bold text-white"
              style={{ backgroundColor: stringToColor(userData?.name || '') }}
            >
              {getInitials(userData?.name || '')}
            </div>
          </div>
        </div>

        {/* Profile Info */}
        <div className="pt-20 text-center">
          <h1 className="text-2xl font-bold text-white mb-1">{userData?.name}</h1>
          <div className="flex flex-col items-center gap-1 text-white/60">
            <span><UserCircleIcon className="h-5 w-5 inline" /> {userData?.email}</span>
            <span>Telefone: {maskPhone(userData?.phone)}</span>
          </div>
        </div>

        {/* Botões Editar Perfil e Sair */}
        <div className="flex gap-4">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate('/edit-profile')}
            className="flex-1 bg-primary text-white py-3 rounded-xl font-medium hover:bg-primary/90 transition-colors text-center"
          >
            Editar Perfil
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleLogout}
            className="flex-1 bg-red-500/20 text-red-400 backdrop-blur-lg rounded-xl p-4 flex items-center justify-center gap-3 hover:bg-red-500/30 transition-colors"
          >
            <ArrowRightOnRectangleIcon className="h-6 w-6" />
            <span>Sair</span>
          </motion.button>
        </div>

        {/* Points Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/10 backdrop-blur-lg rounded-xl p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-white/60 text-sm">Seus DizCoins</h2>
              <motion.p className="text-3xl font-bold text-white">
                <MotionValueDisplay value={xpMotionValue} />
              </motion.p>
            </div>
            <motion.div 
              className="bg-primary/20 text-primary px-4 py-2 rounded-full"
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              Nível <MotionValueDisplay value={levelMotionValue} />
            </motion.div>
          </div>
          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-primary to-accent"
              style={{ width: displayProgress }}
            />
          </div>
          <p className="text-white/60 text-sm mt-2">
            {xpParaProximoNivel(userData?.balance || 0)} DizCoins para o próximo nível
          </p>
        </motion.div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-4">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => handleComingSoon('Ganhar Pontos')}
            className="bg-white/10 backdrop-blur-lg rounded-xl p-4 flex items-center gap-3 h-[56px]"
          >
            <GiftIcon className="h-6 w-6 text-primary" />
            <span className="text-white">Ganhar Pontos</span>
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setIsQRModalOpen(true)}
            className="bg-white/10 backdrop-blur-lg rounded-xl p-4 flex items-center gap-3 h-[56px]"
          >
            <QrCodeIcon className="h-6 w-6 text-primary" />
            <span className="text-white">Meu QRCode</span>
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate('/ranking')}
            className="bg-white/10 backdrop-blur-lg rounded-xl p-4 flex items-center gap-3 h-[56px]"
          >
            <TrophyIcon className="h-6 w-6 text-primary" />
            <span className="text-white">Ranking Pontos</span>
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => handleComingSoon('Loja Pontos')}
            className="bg-white/10 backdrop-blur-lg rounded-xl p-4 flex items-center gap-3 h-[56px]"
          >
            <ShoppingBagIcon className="h-6 w-6 text-primary" />
            <span className="text-white">Loja Pontos</span>
          </motion.button>
        </div>

        {/* QR Code Modal */}
        <Dialog
          open={isQRModalOpen}
          onClose={() => setIsQRModalOpen(false)}
          className="relative z-50"
        >
          <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" aria-hidden="true" />
          
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Dialog.Panel className="mx-auto max-w-sm rounded-xl bg-background p-6">
              <Dialog.Title className="text-xl font-semibold text-white mb-4 text-center">
                Meu QR Code
              </Dialog.Title>
              
              <div className="flex justify-center mb-6">
                <div className="bg-white p-4 rounded-lg">
                  <QRCodeSVG
                    value={user?.uid || ''}
                    size={200}
                    level="H"
                    includeMargin={true}
                  />
                </div>
              </div>

              <div className="flex gap-4">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setIsQRModalOpen(false)}
                  className="flex-1 bg-white/10 text-white py-3 rounded-xl font-medium hover:bg-white/20 transition-colors"
                >
                  Fechar
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => navigate('/scan-qr')}
                  className="flex-1 bg-primary text-white py-3 rounded-xl font-medium hover:bg-primary/90 transition-colors"
                >
                  Ler Código
                </motion.button>
              </div>
            </Dialog.Panel>
          </div>
        </Dialog>

        {/* Coming Soon Modal */}
        <Dialog
          open={isComingSoonModalOpen}
          onClose={() => setIsComingSoonModalOpen(false)}
          className="relative z-50"
        >
          <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" aria-hidden="true" />
          
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Dialog.Panel className="mx-auto max-w-sm rounded-xl bg-background p-6">
              <Dialog.Title className="text-xl font-semibold text-white mb-4 text-center">
                Em Breve!
              </Dialog.Title>
              
              <div className="text-center text-white/80 mb-6">
                <p className="mb-2">A funcionalidade <span className="text-primary font-medium">{comingSoonFeature}</span> estará disponível em breve!</p>
                <p>Fique ligado para não perder as novidades.</p>
              </div>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setIsComingSoonModalOpen(false)}
                className="w-full bg-primary text-white py-3 rounded-xl font-medium hover:bg-primary/90 transition-colors"
              >
                Entendi
              </motion.button>
            </Dialog.Panel>
          </div>
        </Dialog>

        {/* Recent Transactions */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-white">
              Transações Recentes
            </h2>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate('/transactions')}
              className="text-primary text-sm font-medium hover:text-primary/80 transition-colors"
            >
              Ver todas
            </motion.button>
          </div>
          <div className="space-y-3">
            {loadingTransactions ? (
              <div className="flex justify-center py-4">
                <LoadingSpinner />
              </div>
            ) : error ? (
              <div className="text-center text-red-400 py-4">
                {error}
              </div>
            ) : transactions.length > 0 ? (
              transactions.map((transaction) => (
                <motion.div
                  key={transaction.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="bg-white/10 backdrop-blur-lg rounded-xl p-4"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-white font-medium">
                        {transaction.details || transaction.business}
                      </h3>
                      <div className="flex items-center gap-2 text-white/60 text-sm">
                        <ClockIcon className="h-4 w-4" />
                        {new Date(transaction.createdAt || transaction.date).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className={`font-semibold ${
                          (transaction.type === 'earn' || transaction.type === 'earned')
                            ? 'text-green-400'
                            : 'text-red-400'
                        }`}
                      >
                        {(transaction.type === 'earn' || transaction.type === 'earned') ? '+' : '-'}
                        {transaction.amount || transaction.points}
                      </span>
                      <ArrowRightIcon className="h-5 w-5 text-white/60" />
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="text-center text-white/60 py-4">
                Nenhuma transação encontrada
              </div>
            )}
          </div>
        </div>
      </div>
    </PageContainer>
  );
};

export default Profile; 